import { inject, Injectable } from '@angular/core';
import {
  CreateLookupListParams,
  LookupList,
  LookupListData,
  UpdateLookupListParams,
} from '../models/lookup-list.model';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Service for managing reusable lookup lists stored in the local SQLite database.
 *
 * All operations are 100% offline-first — no backend API calls.
 * Changes sync via the existing WebSocket sync mechanism.
 *
 * Lookup lists are stored with:
 * - Top-level `is_active` column (for efficient filtering)
 * - `data` JSON column containing `name` and `options` array
 */
@Injectable({
  providedIn: 'root',
})
export class LookupListService {
  private db = inject(DatabaseService);
  private logger = inject(LoggerService).getLogger('lookup-lists');

  // ---------------------------------------------------------------------------
  // Parsing
  // ---------------------------------------------------------------------------

  private parseLookupListRow(row: any): LookupList {
    const data: LookupListData = row.data ? JSON.parse(row.data) : { name: '', options: [] };
    return {
      id: row.id,
      is_active: row.is_active,
      name: data.name || '',
      options: data.options || [],
      created_by: row.created_by || '',
      created_at: row.created_at || 0,
      updated_by: row.updated_by || '',
      updated_at: row.updated_at || 0,
    };
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  /** Get all lookup lists (active and inactive), ordered by name. */
  async getAll(): Promise<LookupList[]> {
    await this.db.init();
    this.logger.debug('Getting all lookup lists...');
    const results = await this.db.exec(
      `SELECT * FROM lookup_lists ORDER BY json_extract(data, '$.name')`,
    );
    return results.map((row) => this.parseLookupListRow(row));
  }

  /** Get only active lookup lists, ordered by name. */
  async getActive(): Promise<LookupList[]> {
    await this.db.init();
    const results = await this.db.exec(
      `SELECT * FROM lookup_lists WHERE is_active = 1 ORDER BY json_extract(data, '$.name')`,
    );
    return results.map((row) => this.parseLookupListRow(row));
  }

  /** Get a single lookup list by ID. Returns `null` if not found. */
  async getById(id: string): Promise<LookupList | null> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM lookup_lists WHERE id = ?', [id]);
    return results.length > 0 ? this.parseLookupListRow(results[0]) : null;
  }

  // ---------------------------------------------------------------------------
  // Reactive Queries (RQS pattern)
  // ---------------------------------------------------------------------------

  /** Reactive query for all lookup lists (active + inactive). */
  createQuery(): QuerySignal<LookupList[]> {
    return this.db.createQuery('lookup_lists', () => this.getAll());
  }

  /** Reactive query for active lookup lists only. */
  createActiveQuery(): QuerySignal<LookupList[]> {
    return this.db.createQuery('lookup_lists', () => this.getActive());
  }

  /** Reactive query for a single lookup list by ID. */
  createQueryById(id: string): QuerySignal<LookupList | null> {
    return this.db.createQuery('lookup_lists', () => this.getById(id));
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  /** Create a new lookup list. */
  async create(params: CreateLookupListParams): Promise<LookupList> {
    try {
      await this.db.init();

      const id = this.db.generateId();
      const data: LookupListData = {
        name: params.name,
        options: params.options || [],
      };
      const now = Date.now();

      this.logger.debug('Creating lookup list:', { id, name: params.name });

      await this.db.run(
        `INSERT INTO lookup_lists (id, is_active, data, created_by, created_at, updated_by, updated_at)
         VALUES (?, 1, ?, '', ?, '', ?)`,
        [id, JSON.stringify(data), now, now],
      );

      const list = await this.getById(id);
      if (!list) throw new Error('Failed to retrieve lookup list after creation');
      this.logger.debug('Lookup list created:', list);
      return list;
    } catch (error) {
      this.logger.error('Failed to create lookup list:', error);
      throw error;
    }
  }

  /** Update an existing lookup list's name, options, and/or active status. */
  async update(params: UpdateLookupListParams): Promise<LookupList> {
    try {
      await this.db.init();

      const existing = await this.getById(params.id);
      if (!existing) throw new Error(`Lookup list '${params.id}' not found`);

      const data: LookupListData = {
        name: params.name !== undefined ? params.name : existing.name,
        options: params.options !== undefined ? params.options : existing.options,
      };
      const isActive = params.is_active !== undefined ? params.is_active : existing.is_active;
      const now = Date.now();

      await this.db.run(
        `UPDATE lookup_lists SET is_active = ?, data = ?, updated_at = ? WHERE id = ?`,
        [isActive, JSON.stringify(data), now, params.id],
      );

      const updated = await this.getById(params.id);
      if (!updated) throw new Error('Failed to retrieve lookup list after update');
      this.logger.debug('Lookup list updated:', updated);
      return updated;
    } catch (error) {
      this.logger.error('Failed to update lookup list:', error);
      throw error;
    }
  }

  /**
   * Soft-delete (deactivate) a lookup list.
   * The list remains in the database and continues to work in existing forms.
   */
  async deactivate(id: string): Promise<void> {
    try {
      await this.db.init();
      await this.db.run(`UPDATE lookup_lists SET is_active = 0, updated_at = ? WHERE id = ?`, [
        Date.now(),
        id,
      ]);
      this.logger.debug('Lookup list deactivated:', id);
    } catch (error) {
      this.logger.error('Failed to deactivate lookup list:', error);
      throw error;
    }
  }
}
